"use client";

import type { LeadFlowAnswerFieldName, LeadFlowAnswers } from "../types";

import { useEffect } from "react";
import { useLeadFlowAnswerStore } from "../components/LeadFlowAnswerStoreProvider";
import { useLeadFlowStep } from "../components/LeadFlowStepContext";
import { useAnswersLoaded } from "./use-answers";

/**
 * Fills a question's default answer once stored answers have loaded,
 * so a default never overwrites an answer restored from the session or the
 * query string.
 */
function useDefaultAnswer<TFieldName extends LeadFlowAnswerFieldName>(
  fieldName: TFieldName,
  defaultAnswer: LeadFlowAnswers[TFieldName] | undefined,
) {
  const store = useLeadFlowAnswerStore();
  const hasLoadedAnswers = useAnswersLoaded();
  const { isInert = false } = useLeadFlowStep();

  useEffect(() => {
    if (isInert || !hasLoadedAnswers || defaultAnswer === undefined) {
      return;
    }

    if (store.getSnapshot()[fieldName] === undefined) {
      store.setAnswer(fieldName, defaultAnswer);
    }
  }, [defaultAnswer, fieldName, hasLoadedAnswers, isInert, store]);
}

export { useDefaultAnswer };
